/**
 * @fileoverview Chat API hook for handling real-time SSE streaming communication.
 * Manages message sending, JWT authentication, UUID tracking, SSE stream handling, and error recovery.
 *
 * @module hooks/chat/useChatAPI
 */

import { useCallback, useRef } from 'react'
import { AuthenticatedSSE } from '../../utils/sse'
import analyticsService from '../../services/analytics/analytics'
import { useAuth } from '../auth/useAuth'

/**
 * Custom hook for managing chat API interactions with SSE streaming.
 * Handles authentication, message sending, and real-time response streaming.
 *
 * @param {Object} chatState - Chat state management object from useChatState
 * @param {Function} chatState.addUserMessage - Adds a user message to the chat
 * @param {Function} chatState.addAssistantPlaceholder - Creates placeholder for assistant response
 * @param {Function} chatState.addMessagePart - Adds a part to assistant message (new parts API)
 * @param {Function} chatState.removeLastUserMessage - Removes the last user message (on error)
 * @param {Function} chatState.setIsStreaming - Sets streaming state
 * @param {Function} chatState.cleanupSSE - Cleans up existing SSE connections
 * @param {Object} chatState.sseRef - Reference to current SSE connection
 *
 * @param {Object} [options={}] - Configuration options
 * @param {Function} [options.onError] - Error callback function
 * @param {Function} [options.onFirstResponse] - Callback for first response with conversation ID
 *
 * @returns {Object} API methods
 * @returns {Function} returns.sendMessage - Sends a message to the chat API
 *
 * @example
 * const chatState = useChatState()
 * const { sendMessage } = useChatAPI(chatState, {
 *   onError: (error) => console.error('Chat error:', error)
 * })
 *
 * // Send a message
 * await sendMessage('Hello, AI!', turnstileToken)
 */
export const useChatAPI = (chatState, options = {}) => {
  const {
    addUserMessage,
    addAssistantPlaceholder,
    addMessagePart,
    removeLastUserMessage,
    setIsStreaming,
    cleanupSSE,
    sseRef,
    getConversationHistory,
  } = chatState

  const { getCurrentToken, anonymousId, isAuthenticated } = useAuth()
  const hasReceivedFirstResponseRef = useRef(false) // Track if first response received

  /**
   * Sends a message to the chat API and handles the SSE streaming response.
   * Automatically includes appropriate token (client JWT or Supabase JWT) based on auth state.
   * Includes conversation UUID and message UUID for tracking.
   *
   * @param {string} message - The message to send
   * @param {string} [turnstileToken] - Optional Cloudflare Turnstile verification token
   * @returns {Promise<void>} Resolves when streaming is complete
   * @throws {Error} If the API request fails or streaming encounters an error
   */
  /**
   * Properly cleanup after streaming is complete.
   * Sets streaming state to false and performs any necessary cleanup.
   */
  const markStreamingComplete = useCallback(() => {
    setIsStreaming(false)
    // Additional cleanup can be added here if needed
    // For example: finalizing message state, triggering analytics events, etc.
  }, [setIsStreaming])

  const sendMessage = useCallback(
    async (message, turnstileToken) => {
      try {
        // Track chat message event
        analyticsService.trackSearch(message, 'chat')

        // Capture history before adding the new user message
        const previousHistory = getConversationHistory().map((msg) => ({
          role: msg.role,
          content: msg.content,
        }))

        // Add user message (this generates UUIDs and returns the message)
        const userMessage = addUserMessage(message)

        // Get current token (client JWT for anonymous, Supabase JWT for authenticated)
        const jwtToken = await getCurrentToken()

        // Token validation handled silently - no debug logging needed

        // Get the UUIDs directly from the returned user message
        const conversationId = userMessage.conversationId
        const userMessageId = userMessage.messageId

        // Build request body with conversation history for context
        const requestBody = {
          message,
          stream: true,
          turnstileToken,
          conversationHistory: previousHistory,

          // UUID tracking data (conversation and message only)
          conversationId,
          messageId: userMessageId,
        }

        // UUID tracking data included in request

        // Include anonymous ID for authenticated users to enable journey tracking
        if (isAuthenticated && anonymousId) {
          requestBody.anonymousId = anonymousId
        }

        const headers = {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        }

        if (jwtToken) {
          headers['Authorization'] = `Bearer ${jwtToken}`
        }

        // Send request
        const response = await fetch('/api/chat', {
          method: 'POST',
          headers,
          body: JSON.stringify(requestBody),
        })

        if (!response.ok) {
          const errorBody = await response.text()
          console.error('API error response:', errorBody)

          // Try to parse error body for specific error messages
          let specificError = null
          try {
            const errorData = JSON.parse(errorBody)
            if (errorData.errors && errorData.errors.length > 0) {
              const error = errorData.errors[0]
              if (error.title === 'Invalid Turnstile Token') {
                specificError = 'Security verification failed. Please refresh and try again.'
              }
            }
          } catch (e) {
            // Error body is not JSON, use generic message
          }

          throw new Error(
            specificError || `API request failed: ${response.status} ${response.statusText}`
          )
        }

        // Verify we got a streaming response
        const contentType = response.headers.get('content-type')
        if (!contentType || !contentType.includes('text/event-stream')) {
          throw new Error(`Expected streaming response but got: ${contentType}`)
        }

        // Setup streaming
        setIsStreaming(true)
        addAssistantPlaceholder()

        // Reset first response tracking for new message
        hasReceivedFirstResponseRef.current = false

        // Clean up existing SSE
        cleanupSSE()

        // Create SSE handler
        const sse = new AuthenticatedSSE('/api/chat', {
          /**
           * Handles incoming SSE messages from the chat API
           * @param {Object} parsed - Parsed SSE message
           * @param {string} parsed.type - Message type ('status', 'progress', 'response', 'error', 'clickableresponse')
           * @param {string} parsed.message - Message content
           * @param {string} [parsed.message_id] - Message UUID from backend
           * @param {string} [parsed.conversation_id] - Conversation UUID from backend
           * @param {string} [parsed.sentenceUUID] - UUID for clickable responses
           * @param {Object} [parsed.components] - Interactive components for inline rendering
           * @param {number} [parsed.order] - Order field for proper sequencing
           */
          onMessage: (parsed) => {
            // Enhanced validation for SSE message structure
            if (!isValidSSEMessage(parsed)) {
              console.warn('Invalid SSE message received:', parsed)
              return
            }
            const messageType = parsed.type || 'response'
            const messageContent = parsed.message

            // Create part data structure with SSE field names (they will be mapped to storage names in addMessagePart)
            const partData = {
              type: messageType,
              message: messageContent,
              metadata: {
                event_id: parsed.event_id || `sse-${Date.now()}`,
                source: 'chat-api',
              },
            }

            // Pass through SSE field names (they will be mapped to storage names in addMessagePart)
            // Keep SSE naming like sentenceUUID, messageUUID, conversationUUID
            // Don't map them here - addMessagePart will handle the mapping
            if (parsed.message_id || parsed.messageUUID) {
              partData.messageUUID = parsed.message_id || parsed.messageUUID // Keep SSE naming
            }
            if (parsed.conversation_id || parsed.conversationUUID) {
              partData.conversationUUID = parsed.conversation_id || parsed.conversationUUID // Keep SSE naming
            }
            // Always set sentenceUUID - use null if not present
            partData.sentenceUUID = parsed.sentenceUUID || null

            // Pass through ALL SSE fields for storage (they will be handled by addMessagePart)
            // This includes isComponent, componentType, isTLDR, and any other fields from SSE
            const {
              type: _type, // eslint-disable-line no-unused-vars
              message: _message, // eslint-disable-line no-unused-vars
              metadata: _metadata, // eslint-disable-line no-unused-vars
              ...additionalFields
            } = parsed

            // Add all additional fields from SSE to partData
            Object.assign(partData, additionalFields)

            // Handle special response types with specific logic
            if (messageType === 'clickableresponse') {
              // Extract components (sentenceUUID already handled above)
              Object.assign(partData, {
                components: parsed.components || {},
                isComponent: true,
              })
            } else if (messageType === 'response' && parsed.components) {
              // response with components: Mark as isComponent
              Object.assign(partData, {
                components: parsed.components,
                isComponent: true,
              })
            } else if (messageType === 'response' && parsed.sentences) {
              // response with sentences: Mark as hasClickableSentences
              Object.assign(partData, {
                sentences: parsed.sentences,
                hasClickableSentences: true,
              })
            } else if (messageType === 'error') {
              // error: Include trace information
              Object.assign(partData, {
                trace: parsed.trace,
                isError: true,
              })

              // Track SSE error event for analytics
              analyticsService.trackError(`SSE Chat Error: ${messageContent}`, false)
            }

            // Handle order field from SSE messages for proper sequencing
            if (parsed.order && typeof parsed.order === 'number') {
              partData.order = parsed.order
            }

            // Enhanced error logging for debugging
            if (messageType === 'error') {
              console.error('SSE error message:', {
                message: messageContent,
                trace: parsed.trace,
                timestamp: new Date().toISOString(),
              })
            }

            // Use chatState.addMessagePart instead of chatState.updateAssistantMessage
            addMessagePart(partData)

            // Track first response for callback
            if (messageType === 'response' && !hasReceivedFirstResponseRef.current) {
              hasReceivedFirstResponseRef.current = true
              if (options.onFirstResponse && conversationId) {
                options.onFirstResponse(conversationId)
              }
            }
          },
          /**
           * Handles SSE connection errors
           * @param {Error} error - SSE error object
           */
          onError: (error) => {
            console.error('SSE error:', error)
            setIsStreaming(false)
            if (options.onError) {
              options.onError(error)
            }
          },
          /**
           * Handles SSE connection close event
           */
          onClose: () => {
            // Mark streaming as complete properly
            markStreamingComplete()
          },
        })

        sseRef.current = sse
        await sse.readStream(response.body)
      } catch (error) {
        console.error('Chat error:', error)
        removeLastUserMessage()
        setIsStreaming(false)

        // Provide specific error messages
        let errorMessage = 'Failed to send message. Please try again.'

        if (error.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection.'
        } else if (error.message.includes('404')) {
          errorMessage = 'Chat service not found. Please try again later.'
        } else if (error.message.includes('500')) {
          errorMessage = 'Server error. Please try again later.'
        } else if (error.message.includes('429')) {
          errorMessage = 'Too many requests. Please wait a moment and try again.'
        } else if (error.message.includes('token')) {
          errorMessage = 'Authentication error. Please try logging in again.'
        } else if (error.message.includes('quota') || error.message.includes('exceeded')) {
          // If the error message contains rate limit info, use it directly
          errorMessage = error.message
        }

        // Track error event
        analyticsService.trackError(`Chat error: ${errorMessage}`, false)

        if (options.onError) {
          options.onError(new Error(errorMessage))
        }
      }
    },
    [
      addUserMessage,
      addAssistantPlaceholder,
      addMessagePart,
      removeLastUserMessage,
      setIsStreaming,
      cleanupSSE,
      sseRef,
      getConversationHistory,
      getCurrentToken,
      anonymousId,
      isAuthenticated,
      options,
      markStreamingComplete,
    ]
  )

  /**
   * Validates SSE message structure to ensure proper processing.
   * Checks for required fields and proper data types.
   *
   * @param {Object} data - SSE message data to validate
   * @returns {boolean} True if message is valid, false otherwise
   */
  const isValidSSEMessage = (data) => {
    if (!data || typeof data !== 'object') {
      console.warn('SSE message validation failed: not an object')
      return false
    }

    // Check required type field
    if (!['status', 'progress', 'response', 'error', 'clickableresponse'].includes(data.type)) {
      console.warn('SSE message validation failed: invalid type:', data.type)
      return false
    }

    // Check required message field
    if (!data.message || typeof data.message !== 'string') {
      console.warn('SSE message validation failed: invalid message:', data.message)
      return false
    }

    // Additional validation for response messages
    if (data.type === 'response') {
      // Validate order field if present
      if (data.order !== undefined && (typeof data.order !== 'number' || data.order < 1)) {
        console.warn('SSE message validation failed: invalid order:', data.order)
        return false
      }

      // Validate component structure if isComponent is true
      if (data.isComponent && !data.componentType) {
        console.warn('SSE message validation failed: isComponent true but missing componentType')
        return false
      }
    }

    return true
  }

  return {
    sendMessage,
  }
}
